import { Component, input, output, signal, computed, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { FormsModule } from '@angular/forms';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { SwitchModule } from '@progress/kendo-angular-inputs';
import { LoaderModule } from '@progress/kendo-angular-indicators';
import { ConfigurationItem, ConfigurationStatusDetailResponse } from 'src/app/core/models/analytics/es-models';

@Component({
  selector: 'app-es-config-details-dialog',
  templateUrl: './es-config-details-dialog.component.html',
  styleUrls: ['./es-config-details-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, FormsModule, DialogsModule, ButtonsModule, SwitchModule, LoaderModule],
})
export class EsConfigDetailsDialogComponent {
  isVisible = input.required<boolean>();
  configurationData = input<ConfigurationStatusDetailResponse | null>(null);
  isLoading = input<boolean>(false);

  onSave = output<{itemId: string, accepted: boolean, note?: string}[]>();
  onCancel = output<void>();

  // Local state for managing changes (temporary public access for prototype)
  localChanges = signal<Map<string, {accepted: boolean, note?: string}>>(new Map());

  // Computed property to get configuration items with local changes applied
  configurationItems = computed(() => {
    const data = this.configurationData();
    if (!data) return [];
    
    const changes = this.localChanges();
    return data.configurationItems.map(item => ({
      ...item,
      isDeviationAccepted: changes.has(item.id) ? changes.get(item.id)!.accepted : item.isDeviationAccepted,
      exceptionNote: changes.has(item.id) ? changes.get(item.id)!.note : item.exceptionNote
    }));
  });

  // Computed property to check if there are any unsaved changes
  hasUnsavedChanges = computed(() => {
    return this.localChanges().size > 0;
  });

  // Computed property for compliance statistics
  complianceStats = computed(() => {
    const items = this.configurationItems();
    const total = items.length;
    const compliant = items.filter(item => item.currentStatus === 'compliant').length;
    const acceptedDeviations = items.filter(item => item.isDeviationAccepted && item.currentStatus === 'non_compliant').length;
    const nonCompliant = items.filter(item => item.currentStatus === 'non_compliant' && !item.isDeviationAccepted).length;
    
    return {
      total,
      compliant,
      acceptedDeviations,
      nonCompliant,
      compliancePercentage: Math.round(((compliant + acceptedDeviations) / total) * 100)
    };
  });

  updateDeviation(itemId: string, accepted: boolean): void {
    const currentChanges = new Map(this.localChanges());
    const item = this.configurationItems().find(i => i.id === itemId);
    
    if (item) {
      currentChanges.set(itemId, {
        accepted,
        note: accepted ? (currentChanges.get(itemId)?.note || item.exceptionNote || '') : undefined
      });
      this.localChanges.set(currentChanges);
    }
  }

  updateNote(itemId: string, note: string): void {
    const currentChanges = new Map(this.localChanges());
    const existingChange = currentChanges.get(itemId);
    
    if (existingChange) {
      currentChanges.set(itemId, {
        ...existingChange,
        note
      });
      this.localChanges.set(currentChanges);
    }
  }

  getStatusIcon(item: ConfigurationItem): string {
    if (item.currentStatus === 'compliant') {
      return '✅';
    } else if (item.isDeviationAccepted) {
      return '⚠️';
    } else {
      return '❌';
    }
  }

  getStatusText(item: ConfigurationItem): string {
    if (item.currentStatus === 'compliant') {
      return 'Compliant';
    } else if (item.isDeviationAccepted) {
      return 'Deviation Accepted';
    } else {
      return 'Non-Compliant';
    }
  }

  getStatusClass(item: ConfigurationItem): string {
    if (item.currentStatus === 'compliant') {
      return 'status-compliant';
    } else if (item.isDeviationAccepted) {
      return 'status-accepted';
    } else {
      return 'status-non-compliant';
    }
  }

  onSaveChanges(): void {
    const changes = Array.from(this.localChanges().entries()).map(([itemId, change]) => ({
      itemId,
      accepted: change.accepted,
      note: change.note
    }));
    
    this.onSave.emit(changes);
    this.localChanges.set(new Map()); // Clear local changes after save
  }

  onCancelChanges(): void {
    this.localChanges.set(new Map()); // Clear local changes
    this.onCancel.emit();
  }
}